/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  CUSTOM_ELEMENTS_SCHEMA,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, interval, takeWhile } from 'rxjs';
import * as XLSX from 'xlsx';

import {
  DialogRef,
  DialogService,
  ButtonComponent,
  IconComponent,
  ConfirmDialogComponent,
  FileUploadComponent,
  UploadedFile,
  TableComponent,
  TableConfig,
  ActionEvent,
  AlertComponent,
  CheckboxComponent,
} from 'nebula-ui-kit';
import { ImportarAsientoService } from '../../data-access/importar-asiento/importar-asiento.service';
import type { ImportarAsientoDatosCabecera } from '../../data-access/importar-asiento/importar-asiento.service';

export interface AsientoContableLinea {
  id: string;
  codCuenta: string;
  nombreCuenta: string;
  valorDebito: number;
  valorCredito: number;
  baseDeduccion: number;
  codCC: string;
  noIdentificacion: string;
  nombreRazonSocial: string;
}

export interface ImportarAsientoResponse {
  lineas: AsientoContableLinea[];
  totalDebito: number;
  totalCredito: number;
  diferencia: number;
}

type StepType = 1 | 2 | 3;

@Component({
  selector: 'app-importar-asiento-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    IconComponent,
    FileUploadComponent,
    TableComponent,
    AlertComponent,
    CheckboxComponent,
  ],
  templateUrl: './importar-asiento-dialog.component.html',
  styleUrls: ['./importar-asiento-dialog.component.scss'],
})
export class ImportarAsientoDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(DialogRef, { optional: true });
  readonly dialogService = inject(DialogService);
  readonly cdr = inject(ChangeDetectorRef);
  private readonly importarAsientoService = inject(ImportarAsientoService);

  @Input() totalFactura = 0;
  @Input() requiereAprobacion = false;
  @Input() datosCabecera?: ImportarAsientoDatosCabecera;
  @Input() skipBackendEnrichment = false;
  @Output() asientoGuardado = new EventEmitter<ImportarAsientoResponse>();

  loading = signal(false);
  procesandoArchivo = signal(false);
  readonly destroy$ = new Subject<void>();
  private progressSubscription: any = null;
  private columnasArchivo = 6;
  private readonly COLUMNAS_PLANTILLA_LEGACY = 6;
  private readonly COLUMNAS_PLANTILLA_HT016 = 8;

  currentStep = signal<StepType>(1);
  step1Completed = signal(false);
  step2Completed = signal(false);

  archivoSeleccionado = signal<UploadedFile | null>(null);
  archivoOriginal: File | null = null;
  nombreArchivo = signal('');
  tipoArchivo = signal('');
  tieneEncabezado = signal(true);

  mostrarAlerta = signal(false);
  alertaTipo = signal<'success' | 'error' | 'warning' | 'info'>('info');
  alertaTitulo = signal('');
  alertaMensaje = signal('');

  progreso = signal(0);
  mensajeProcesamiento = signal('Preparando archivo para procesar...');
  estadoProcesamiento = signal('Iniciando...');
  totalLineasAProcesar = signal(0);
  procesamientoCompletado = signal(false);

  lineas = signal<AsientoContableLinea[]>([]);
  totalDebito = computed(() => this.calcularTotalDebito());
  totalCredito = computed(() => this.calcularTotalCredito());
  diferencia = computed(() => this.totalDebito() - this.totalCredito());

  private readonly cuentaNombresMap: Record<string, string> = {
    '510506': 'Gastos Operacionales',
    '220505': 'Proveedores Nacionales',
    '135515': 'Retención en la Fuente',
    '240801': 'IVA Descontable',
    '513005': 'Gastos Administrativos',
  };

  tableConfig = computed<TableConfig<AsientoContableLinea>>(() => ({
    columns: [
      { key: 'codCuenta', label: 'Cód. Cuenta', sortable: true, visible: true },
      {
        key: 'valorDebito',
        label: 'Valor Débito',
        sortable: true,
        visible: true,
        format: (value: number) => this.formatCurrency(value),
      },
      {
        key: 'valorCredito',
        label: 'Valor Crédito',
        sortable: true,
        visible: true,
        format: (value: number) => this.formatCurrency(value),
      },
      {
        key: 'baseDeduccion',
        label: 'Base Deducción',
        sortable: true,
        visible: true,
        format: (value: number) => this.formatCurrency(value),
      },
      { key: 'codCC', label: 'Cód. CC', sortable: true, visible: true },
      { key: 'noIdentificacion', label: 'No. Identificación', sortable: true, visible: true },
    ],
    actions: [
      {
        action: 'delete',
        label: 'Eliminar',
        icon: 'Trash2',
        tooltip: 'Eliminar línea',
        className: 'text-danger',
      },
    ],
    rowClickable: false,
    showPagination: true,
    pageSize: 10,
    pageSizeOptions: [5, 10, 25, 50],
    ariaLabel: 'Tabla de asiento contable',
  }));

  ngOnInit(): void {
    const data = (this.dialogRef as { data?: Record<string, unknown> } | null)?.data;
    if (!data) return;

    if (data['totalFactura'] != null) {
      this.totalFactura = Number(data['totalFactura']);
    }
    if (data['requiereAprobacion'] != null) {
      this.requiereAprobacion = Boolean(data['requiereAprobacion']);
    }
    if (data['datosCabecera']) {
      this.datosCabecera = data['datosCabecera'] as ImportarAsientoDatosCabecera;
    }
    if (data['skipBackendEnrichment'] != null) {
      this.skipBackendEnrichment = Boolean(data['skipBackendEnrichment']);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  }

  getNombreCuenta(codCuenta: string): string {
    return this.cuentaNombresMap[codCuenta] || 'Cuenta Contable';
  }

  onTableAction(event: ActionEvent<AsientoContableLinea>): void {
    if (event.action === 'delete') {
      this.eliminarLineaPorId(event.data.id);
    }
  }

  async eliminarLineaPorId(id: string): Promise<void> {
    const confirmed = await this.showConfirmDialog(
      '¿Eliminar línea?',
      '¿Estás seguro de que deseas eliminar esta línea?',
      'warning',
    );

    if (confirmed) {
      this.lineas.update((lineas) => lineas.filter((linea) => linea.id !== id));
      this.calcularTotales();
      this.mostrarAlertaExito('Línea eliminada correctamente');
    }
  }

  private calcularTotalDebito(): number {
    return this.lineas().reduce((sum, linea) => sum + (Number(linea.valorDebito) || 0), 0);
  }

  private calcularTotalCredito(): number {
    return this.lineas().reduce((sum, linea) => sum + (Number(linea.valorCredito) || 0), 0);
  }

  calcularTotales(): void {
    this.cdr.markForCheck();
  }

  validarTotalFactura(): boolean {
    return this.totalDebito() === this.totalFactura;
  }

  isFormularioValido(): boolean {
    return this.diferencia() === 0 && this.lineas().length > 0;
  }

  onFilesSelected(files: UploadedFile[]): void {
    if (files.length === 0) return;

    if (files.length > 1) {
      this.mostrarAlertaError('Solo se puede seleccionar un archivo a la vez');
      return;
    }

    const file = files[0];
    this.archivoSeleccionado.set(file);
    this.archivoOriginal = file.file;
    this.nombreArchivo.set(file.name);
    const extension = file.name.split('.').pop()?.toUpperCase() || '';
    this.tipoArchivo.set(extension);
    this.cdr.markForCheck();
  }

  limpiarArchivo(): void {
    this.archivoSeleccionado.set(null);
    this.archivoOriginal = null;
    this.nombreArchivo.set('');
    this.tipoArchivo.set('');
    this.columnasArchivo = this.COLUMNAS_PLANTILLA_LEGACY;
    this.tieneEncabezado.set(true);
  }

  async procesarArchivoSeleccionado(): Promise<void> {
    if (!this.archivoSeleccionado()) return;

    this.procesandoArchivo.set(true);

    try {
      await this.procesarArchivo(this.archivoSeleccionado()!.file);
      this.step1Completed.set(true);
      this.currentStep.set(2);
      this.cdr.detectChanges();
    } catch (error: any) {
      this.mostrarAlertaError(
        error.message || 'Error al procesar el archivo. Verifique el formato.',
      );
    } finally {
      this.procesandoArchivo.set(false);
    }
  }

  private esCantidadColumnasValida(count: number): boolean {
    return count === this.COLUMNAS_PLANTILLA_LEGACY || count === this.COLUMNAS_PLANTILLA_HT016;
  }

  private mensajeColumnasInvalidas(count: number): string {
    return `El archivo debe tener ${this.COLUMNAS_PLANTILLA_LEGACY} columnas (formato simple) u ${this.COLUMNAS_PLANTILLA_HT016} columnas (plantilla exportada HT016). Se encontraron ${count} columnas.`;
  }

  private esFilaVacia(row: any[] | undefined): boolean {
    if (!row?.length) {
      return true;
    }

    return row.every((cell) => cell === null || cell === undefined || String(cell).trim() === '');
  }

  private contarColumnasEnFila(row: any[]): number {
    if (!row?.length) {
      return 0;
    }

    let lastIndex = -1;
    for (let i = 0; i < row.length; i++) {
      const value = row[i];
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        lastIndex = i;
      }
    }

    if (lastIndex < 0) {
      return 0;
    }

    if (lastIndex >= 6) {
      return this.COLUMNAS_PLANTILLA_HT016;
    }

    return this.COLUMNAS_PLANTILLA_LEGACY;
  }

  private normalizarValoresFila(values: string[]): string[] {
    const normalizados = [...values];
    while (normalizados.length < this.columnasArchivo) {
      normalizados.push('');
    }
    return normalizados.slice(0, this.columnasArchivo);
  }

  private extraerCamposLinea(values: string[]): {
    codCuenta: string;
    valorDebito: number;
    valorCredito: number;
    baseDeduccion: number;
    codCC: string;
    noIdentificacion: string;
  } {
    const normalizados = this.normalizarValoresFila(values);

    if (this.columnasArchivo === this.COLUMNAS_PLANTILLA_HT016) {
      return {
        codCuenta: normalizados[0]?.trim() || '',
        valorDebito: this.parseNumber(normalizados[1]),
        valorCredito: this.parseNumber(normalizados[2]),
        baseDeduccion: this.parseNumber(normalizados[3]),
        codCC: normalizados[6]?.trim() || '',
        noIdentificacion: normalizados[7]?.trim() || '',
      };
    }

    return {
      codCuenta: normalizados[0]?.trim() || '',
      valorDebito: this.parseNumber(normalizados[1]),
      valorCredito: this.parseNumber(normalizados[2]),
      baseDeduccion: this.parseNumber(normalizados[3]),
      codCC: normalizados[4]?.trim() || '',
      noIdentificacion: normalizados[5]?.trim() || '',
    };
  }

  private procesarArchivo(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const extension = file.name.split('.').pop()?.toLowerCase();

      if (extension === 'csv') {
        this.procesarCSV(file, resolve, reject);
      } else if (extension === 'xlsx' || extension === 'xls') {
        this.procesarExcel(file, resolve, reject);
      } else if (extension === 'txt') {
        this.procesarTXT(file, resolve, reject);
      } else {
        reject(new Error('Formato no soportado. Use CSV, TXT o Excel.'));
      }
    });
  }

  private procesarTXT(file: File, resolve: () => void, reject: (error: Error) => void): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter((line) => line.trim());

        const { startIndex, separator } = this.validateAndGetHeaders(lines, reject);
        if (!startIndex) return;

        const { nuevasLineas, errores } = this.parseAndValidateLines(lines, startIndex, separator);

        if (errores.length > 0) {
          reject(new Error(`Errores de validación:\n${errores.join('\n')}`));
          return;
        }

        if (nuevasLineas.length === 0) {
          reject(new Error('No se encontraron datos válidos en el archivo TXT'));
          return;
        }

        this.lineas.set(nuevasLineas);
        this.calcularTotales();
        this.notifyBalanceResult();
        resolve();
      } catch (error) {
        reject(new Error('Error al parsear el archivo TXT: ' + (error as Error).message));
      }
    };
    reader.onerror = () => reject(new Error('Error al leer el archivo'));
    reader.readAsText(file, 'UTF-8');
  }

  private validateAndGetHeaders(
    lines: string[],
    reject: (error: Error) => void,
  ): { startIndex: number | null; separator: string } {
    const minLinesRequired = this.tieneEncabezado() ? 2 : 1;
    if (lines.length < minLinesRequired) {
      reject(
        new Error(
          this.tieneEncabezado()
            ? 'El archivo TXT no tiene datos suficientes'
            : 'El archivo TXT no tiene datos',
        ),
      );
      return { startIndex: null, separator: '' };
    }

    const firstLine = lines[0];
    const separator = this.detectarDelimitador(firstLine);
    let startIndex = 0;

    if (this.tieneEncabezado()) {
      const headers = this.parseCSVLine(lines[0], separator);
      if (!this.esCantidadColumnasValida(headers.length)) {
        reject(new Error(this.mensajeColumnasInvalidas(headers.length)));
        return { startIndex: null, separator: '' };
      }
      this.columnasArchivo = headers.length;
      startIndex = 1;
    } else {
      const firstDataLine = this.parseCSVLine(lines[0], separator);
      if (!this.esCantidadColumnasValida(firstDataLine.length)) {
        reject(new Error(this.mensajeColumnasInvalidas(firstDataLine.length)));
        return { startIndex: null, separator: '' };
      }
      this.columnasArchivo = firstDataLine.length;
    }

    return { startIndex, separator };
  }

  private parseAndValidateLines(
    lines: string[],
    startIndex: number,
    separator: string,
  ): { nuevasLineas: AsientoContableLinea[]; errores: string[] } {
    const nuevasLineas: AsientoContableLinea[] = [];
    const errores: string[] = [];

    for (let i = startIndex; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i], separator);

      if (values.length === 0 || values.every((v) => !v || v.trim() === '')) {
        continue;
      }

      while (values.length < this.columnasArchivo) values.push('');

      const result = this.createLineaFromValues(values, i + 1);
      if (!result.linea) {
        errores.push(...result.errores);
      } else if (
        result.linea.codCuenta &&
        (result.linea.valorDebito > 0 || result.linea.valorCredito > 0)
      ) {
        nuevasLineas.push(result.linea);
      }
    }

    return { nuevasLineas, errores };
  }

  private createLineaFromValues(
    values: string[],
    filaNumero: number,
  ): { linea: AsientoContableLinea | null; errores: string[] } {
    const campos = this.extraerCamposLinea(values);

    const validacion = this.validarLineaCompleta(
      campos.codCuenta,
      campos.valorDebito,
      campos.valorCredito,
      campos.codCC,
      campos.noIdentificacion,
      filaNumero,
    );

    if (!validacion.valid) {
      return { linea: null, errores: validacion.errores };
    }

    return {
      linea: {
        id: crypto.randomUUID(),
        codCuenta: String(campos.codCuenta),
        nombreCuenta: this.getNombreCuenta(String(campos.codCuenta)),
        valorDebito: campos.valorDebito,
        valorCredito: campos.valorCredito,
        baseDeduccion: campos.baseDeduccion,
        codCC: String(campos.codCC || ''),
        noIdentificacion: String(campos.noIdentificacion || ''),
        nombreRazonSocial: '',
      },
      errores: [],
    };
  }

  private notifyBalanceResult(): void {
    const diff = this.totalDebito() - this.totalCredito();
    if (diff !== 0) {
      this.mostrarAlertaError(
        `El asiento no está cuadrado. Diferencia: ${this.formatCurrency(diff)}`,
      );
    } else {
      this.mostrarAlertaExito(
        `Se importaron ${this.lineas().length} líneas correctamente. Asiento cuadrado.`,
      );
    }
  }

  private detectarDelimitador(line: string): string {
    const delimitadores = [',', '\t', '|', ';'];

    for (const delim of delimitadores) {
      if (line.includes(delim)) {
        const columnCount = line.split(delim).length;
        if (this.esCantidadColumnasValida(columnCount)) {
          return delim;
        }
      }
    }

    return ',';
  }

  private validarNumeroColumnas(
    headerRow: any[],
    rawData: any[][],
  ): { valid: boolean; error?: string } {
    const columnasHeader = this.contarColumnasEnFila(headerRow || []);
    if (!this.esCantidadColumnasValida(columnasHeader)) {
      return {
        valid: false,
        error: this.mensajeColumnasInvalidas(columnasHeader),
      };
    }

    this.columnasArchivo = columnasHeader;

    for (let i = 1; i < rawData.length; i++) {
      const row = rawData[i];
      if (this.esFilaVacia(row)) {
        continue;
      }

      const columnasFila = this.contarColumnasEnFila(row || []);
      if (columnasFila > this.columnasArchivo) {
        return {
          valid: false,
          error: `Fila ${i + 1} tiene ${columnasFila} columnas. El máximo permitido es ${this.columnasArchivo}.`,
        };
      }
    }

    return { valid: true };
  }

  private validarDebitoCredito(
    valorDebito: number,
    valorCredito: number,
    filaNumero: number,
  ): { valid: boolean; error?: string } {
    if (valorDebito < 0) {
      return {
        valid: false,
        error: `Fila ${filaNumero}: Valor Débito (${valorDebito}) no puede ser negativo`,
      };
    }
    if (valorCredito < 0) {
      return {
        valid: false,
        error: `Fila ${filaNumero}: Valor Crédito (${valorCredito}) no puede ser negativo`,
      };
    }

    if (valorDebito === 0 && valorCredito === 0) {
      return { valid: false, error: `Fila ${filaNumero}: Débito y Crédito no pueden ser ambos 0` };
    }

    if (valorDebito > 0 && valorCredito > 0) {
      return {
        valid: false,
        error: `Fila ${filaNumero}: No puede tener Débito y Crédito > 0 al mismo tiempo`,
      };
    }

    return { valid: true };
  }

  private validarLineaCompleta(
    codCuenta: string,
    valorDebito: number,
    valorCredito: number,
    codCC: string,
    noIdentificacion: string,
    filaNumero: number,
  ): { valid: boolean; errores: string[] } {
    const errores: string[] = [];

    if (!codCuenta || codCuenta.trim() === '') {
      errores.push(`Fila ${filaNumero}: Código Cuenta es obligatorio`);
    }

    const dcValid = this.validarDebitoCredito(valorDebito, valorCredito, filaNumero);
    if (!dcValid.valid) errores.push(dcValid.error!);

    return { valid: errores.length === 0, errores };
  }
  private procesarCSV(file: File, resolve: () => void, reject: (error: Error) => void): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter((line) => line.trim());

        const csvData = this.validateAndParseCSV(lines);
        if (!csvData.isValid) {
          reject(new Error(csvData.error));
          return;
        }

        const { nuevasLineas, errores } = this.processCSVLines(
          lines,
          csvData.startIndex!,
          csvData.separator!,
        );

        if (errores.length > 0) {
          reject(new Error(`Errores de validación:\n${errores.join('\n')}`));
          return;
        }

        if (nuevasLineas.length === 0) {
          reject(new Error('No se encontraron datos válidos en el archivo CSV'));
          return;
        }

        this.lineas.set(nuevasLineas);
        this.calcularTotales();
        this.notifyBalanceResult();
        resolve();
      } catch (error) {
        reject(new Error('Error al parsear el archivo CSV: ' + (error as Error).message));
      }
    };
    reader.onerror = () => reject(new Error('Error al leer el archivo'));
    reader.readAsText(file, 'UTF-8');
  }

  private validateAndParseCSV(lines: string[]): {
    isValid: boolean;
    error?: string;
    startIndex?: number;
    separator?: string;
  } {
    const minLinesRequired = this.tieneEncabezado() ? 2 : 1;
    if (lines.length < minLinesRequired) {
      return {
        isValid: false,
        error: this.tieneEncabezado()
          ? 'El archivo CSV no tiene datos suficientes'
          : 'El archivo CSV no tiene datos',
      };
    }

    const firstLine = lines[0];
    const separator = this.detectCSVSeparator(firstLine);
    let startIndex = 0;

    if (this.tieneEncabezado()) {
      const headers = this.parseCSVLine(lines[0], separator);
      if (!this.esCantidadColumnasValida(headers.length)) {
        return {
          isValid: false,
          error: this.mensajeColumnasInvalidas(headers.length),
        };
      }
      this.columnasArchivo = headers.length;
      startIndex = 1;
    } else {
      const firstDataLine = this.parseCSVLine(lines[0], separator);
      if (!this.esCantidadColumnasValida(firstDataLine.length)) {
        return {
          isValid: false,
          error: this.mensajeColumnasInvalidas(firstDataLine.length),
        };
      }
      this.columnasArchivo = firstDataLine.length;
      startIndex = 0;
    }

    return { isValid: true, startIndex, separator };
  }

  private detectCSVSeparator(firstLine: string): string {
    if (firstLine.includes(';')) return ';';
    if (firstLine.includes(',')) return ',';
    return ',';
  }

  private processCSVLines(
    lines: string[],
    startIndex: number,
    separator: string,
  ): { nuevasLineas: AsientoContableLinea[]; errores: string[] } {
    const nuevasLineas: AsientoContableLinea[] = [];
    const errores: string[] = [];

    for (let i = startIndex; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i], separator);

      if (values.length === 0 || values.every((v) => !v || v.trim() === '')) {
        continue;
      }

      while (values.length < this.columnasArchivo) values.push('');

      const result = this.createLineaFromCSVValues(values, i + 1);

      if (!result.isValid) {
        errores.push(...result.errores);
        continue;
      }

      if (result.linea) {
        nuevasLineas.push(result.linea);
      }
    }

    return { nuevasLineas, errores };
  }

  private createLineaFromCSVValues(
    values: string[],
    filaNumero: number,
  ): { isValid: boolean; linea?: AsientoContableLinea; errores: string[] } {
    const campos = this.extraerCamposLinea(values);

    const validacion = this.validarLineaCompleta(
      campos.codCuenta,
      campos.valorDebito,
      campos.valorCredito,
      campos.codCC,
      campos.noIdentificacion,
      filaNumero,
    );

    if (!validacion.valid) {
      return { isValid: false, errores: validacion.errores };
    }

    if (campos.codCuenta && (campos.valorDebito > 0 || campos.valorCredito > 0)) {
      return {
        isValid: true,
        linea: {
          id: crypto.randomUUID(),
          codCuenta: String(campos.codCuenta),
          nombreCuenta: this.getNombreCuenta(String(campos.codCuenta)),
          valorDebito: campos.valorDebito,
          valorCredito: campos.valorCredito,
          baseDeduccion: campos.baseDeduccion,
          codCC: String(campos.codCC || ''),
          noIdentificacion: String(campos.noIdentificacion || ''),
          nombreRazonSocial: '',
        },
        errores: [],
      };
    }

    return { isValid: false, errores: [] };
  }
  private parseCSVLine(line: string, separator: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === separator && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);

    return result.map((val) => {
      let cleaned = val.trim();
      if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
        cleaned = cleaned.slice(1, -1);
      }
      return cleaned;
    });
  }

  private cleanCSVValue(value: string): string {
    let cleaned = value.trim();
    if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
      cleaned = cleaned.slice(1, -1);
    }
    return cleaned;
  }

  private parseNumber(value: any): number {
    if (!value || value === '') return 0;

    if (typeof value === 'number') return value;

    let cleanValue = String(value).trim();

    if (cleanValue.startsWith('"') && cleanValue.endsWith('"')) {
      cleanValue = cleanValue.slice(1, -1);
    }

    cleanValue = cleanValue.replace(/'/g, '');

    if (cleanValue === '') return 0;

    if (cleanValue.includes(',') && cleanValue.includes('.')) {
      const lastComma = cleanValue.lastIndexOf(',');
      const lastDot = cleanValue.lastIndexOf('.');

      if (lastDot > lastComma) {
        cleanValue = cleanValue.replace(/,/g, '');
      } else {
        cleanValue = cleanValue.replace(/\./g, '').replace(',', '.');
      }
    } else if (cleanValue.includes(',') && !cleanValue.includes('.')) {
      const commaCount = (cleanValue.match(/,/g) || []).length;
      if (commaCount === 1 && cleanValue.length - cleanValue.indexOf(',') <= 4) {
        cleanValue = cleanValue.replace(',', '.');
      } else {
        cleanValue = cleanValue.replace(/,/g, '');
      }
    } else if (cleanValue.includes('.') && !cleanValue.includes(',')) {
      const dotCount = (cleanValue.match(/\./g) || []).length;
      if (dotCount === 1 && cleanValue.length - cleanValue.indexOf('.') <= 4) {
        // to do
      } else {
        cleanValue = cleanValue.replace(/\./g, '');
      }
    }

    const num = Number(cleanValue);
    return isNaN(num) ? 0 : num;
  }

  private procesarExcel(file: File, resolve: () => void, reject: (error: Error) => void): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });

        const validation = this.validateExcelStructure(rawData as any[][]);
        if (!validation.isValid) {
          reject(new Error(validation.error));
          return;
        }

        const result = this.processExcelRows(rawData as any[][], validation.startIndex!);

        if (result.errores.length > 0) {
          reject(new Error(`Errores de validación:\n${result.errores.join('\n')}`));
          return;
        }

        if (result.nuevasLineas.length === 0) {
          reject(new Error('No se encontraron datos válidos en el archivo Excel'));
          return;
        }

        this.lineas.set(result.nuevasLineas);
        this.calcularTotales();
        this.notifyBalanceResult();
        resolve();
      } catch (error) {
        reject(new Error('Error al parsear el archivo Excel: ' + (error as Error).message));
      }
    };
    reader.onerror = () => reject(new Error('Error al leer el archivo'));
    reader.readAsArrayBuffer(file);
  }

  private validateExcelStructure(rawData: any[][]): {
    isValid: boolean;
    error?: string;
    startIndex?: number;
  } {
    const minRowsRequired = this.tieneEncabezado() ? 2 : 1;
    if (!rawData || rawData.length < minRowsRequired) {
      return {
        isValid: false,
        error: this.tieneEncabezado()
          ? 'El archivo Excel no tiene datos suficientes'
          : 'El archivo Excel no tiene datos',
      };
    }

    if (this.tieneEncabezado()) {
      const estructuraValida = this.validarNumeroColumnas(rawData[0] as any[], rawData);
      if (!estructuraValida.valid) {
        return { isValid: false, error: estructuraValida.error };
      }
      return { isValid: true, startIndex: 1 };
    } else {
      const firstRow = rawData[0] as any[];
      const columnasFila = this.contarColumnasEnFila(firstRow || []);
      if (!this.esCantidadColumnasValida(columnasFila)) {
        return {
          isValid: false,
          error: this.mensajeColumnasInvalidas(columnasFila),
        };
      }
      this.columnasArchivo = columnasFila;
      return { isValid: true, startIndex: 0 };
    }
  }

  private processExcelRows(
    rawData: any[][],
    startIndex: number,
  ): { nuevasLineas: AsientoContableLinea[]; errores: string[] } {
    const nuevasLineas: AsientoContableLinea[] = [];
    const errores: string[] = [];

    for (let i = startIndex; i < rawData.length; i++) {
      const row = rawData[i] as any[];

      if (!row || this.esFilaVacia(row)) {
        continue;
      }

      const rowValues = this.extractExcelRowValues(row, i + 1);

      if (this.shouldSkipExcelRow(rowValues, i, startIndex)) {
        continue;
      }

      const validacion = this.validarLineaCompleta(
        rowValues.codCuenta,
        rowValues.valorDebito,
        rowValues.valorCredito,
        rowValues.codCC,
        rowValues.noIdentificacion,
        rowValues.filaNumero,
      );

      if (!validacion.valid) {
        errores.push(...validacion.errores);
        continue;
      }

      if (rowValues.codCuenta && (rowValues.valorDebito > 0 || rowValues.valorCredito > 0)) {
        nuevasLineas.push(this.createAsientoLineaFromExcel(rowValues));
      }
    }

    return { nuevasLineas, errores };
  }

  private extractExcelRowValues(
    row: any[],
    filaNumero: number,
  ): {
    codCuenta: string;
    valorDebito: number;
    valorCredito: number;
    baseDeduccion: number;
    codCC: string;
    noIdentificacion: string;
    filaNumero: number;
  } {
    const valores = row.map((cell) => {
      if (cell === null || cell === undefined) return '';
      return String(cell).trim();
    });
    const campos = this.extraerCamposLinea(valores);

    return {
      ...campos,
      filaNumero,
    };
  }

  private shouldSkipExcelRow(
    rowValues: ReturnType<typeof this.extractExcelRowValues>,
    index: number,
    startIndex: number,
  ): boolean {
    if (!this.tieneEncabezado() && index === startIndex) {
      const isNaNValue = isNaN(Number(rowValues.codCuenta));
      const hasValue = rowValues.codCuenta.length > 0;
      if (isNaNValue && hasValue) {
        console.warn(
          `Fila ${rowValues.filaNumero} parece ser un encabezado, saltando:`,
          rowValues.codCuenta,
        );
        return true;
      }
    }
    return false;
  }

  private createAsientoLineaFromExcel(
    rowValues: ReturnType<typeof this.extractExcelRowValues>,
  ): AsientoContableLinea {
    return {
      id: crypto.randomUUID(),
      codCuenta: String(rowValues.codCuenta),
      nombreCuenta: this.getNombreCuenta(String(rowValues.codCuenta)),
      valorDebito: rowValues.valorDebito,
      valorCredito: rowValues.valorCredito,
      baseDeduccion: rowValues.baseDeduccion,
      codCC: String(rowValues.codCC || ''),
      noIdentificacion: String(rowValues.noIdentificacion || ''),
      nombreRazonSocial: '',
    };
  }

  private getExcelValuePorPosicion(row: any[], index: number): string {
    if (index >= row.length) return '';
    const value = row[index];
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number') return String(value);
    return String(value || '').trim();
  }

  private getExcelNumberPorPosicion(row: any[], index: number): number {
    if (index >= row.length) return 0;
    const value = row[index];
    if (value === null || value === undefined || value === '') return 0;

    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      let cleaned = value.trim();
      if (cleaned.includes(',') && !cleaned.includes('.')) {
        cleaned = cleaned.replace(',', '.');
      } else if (cleaned.includes(',') && cleaned.includes('.')) {
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
      }
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    }
    return 0;
  }

  limpiarTabla(): void {
    this.lineas.set([]);
    this.calcularTotales();
    this.mostrarAlertaExito('Todas las líneas han sido eliminadas');
  }

  volverStep1(): void {
    this.currentStep.set(1);
    this.step1Completed.set(false);
    this.step2Completed.set(false);
    this.cerrarAlerta();
    this.cdr.detectChanges();
  }

  irStep3(): void {
    if (this.diferencia() === 0 && this.lineas().length > 0) {
      this.step2Completed.set(true);
      this.currentStep.set(3);
      this.cerrarAlerta();
      this.iniciarProcesamientoSimulado();
      this.cdr.detectChanges();
    } else {
      if (this.diferencia() !== 0) {
        this.mostrarAlertaError(
          `La diferencia debe ser cero para continuar. Diferencia actual: ${this.formatCurrency(this.diferencia())}`,
        );
      } else if (this.lineas().length === 0) {
        this.mostrarAlertaError('Debe haber al menos una línea en el asiento');
      }
    }
  }

  private iniciarProcesamientoSimulado(): void {
    this.progreso.set(0);
    this.procesamientoCompletado.set(false);
    this.totalLineasAProcesar.set(this.lineas().length);

    const etapas = [
      { progreso: 10, mensaje: 'Validando estructura del archivo...', estado: 'Validando...' },
      { progreso: 25, mensaje: 'Leyendo cuentas contables...', estado: 'Leyendo cuentas...' },
      { progreso: 40, mensaje: 'Validando códigos de cuenta...', estado: 'Validando códigos...' },
      {
        progreso: 55,
        mensaje: 'Procesando valores débito/crédito...',
        estado: 'Procesando valores...',
      },
      { progreso: 70, mensaje: 'Verificando centros de costo...', estado: 'Verificando CC...' },
      {
        progreso: 85,
        mensaje: 'Calculando totales y diferencias...',
        estado: 'Calculando totales...',
      },
      { progreso: 100, mensaje: 'Procesamiento completado exitosamente', estado: 'Completado' },
    ];

    let etapaActual = 0;

    const interval$ = interval(800).pipe(takeWhile(() => etapaActual < etapas.length));

    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }

    this.progressSubscription = interval$.subscribe(() => {
      const etapa = etapas[etapaActual];
      this.progreso.set(etapa.progreso);
      this.mensajeProcesamiento.set(etapa.mensaje);
      this.estadoProcesamiento.set(etapa.estado);
      this.cdr.detectChanges();

      if (etapa.progreso === 100) {
        this.procesamientoCompletado.set(true);
        if (this.progressSubscription) {
          this.progressSubscription.unsubscribe();
        }
        this.cdr.detectChanges();
      }

      etapaActual++;
    });
  }

  finalizarProcesamiento(): void {
    if (this.procesamientoCompletado()) {
      this.onGuardar();
    }
  }

  async onGuardar(): Promise<void> {
    if (!this.isFormularioValido()) return;

    this.loading.set(true);

    const resultado: ImportarAsientoResponse = {
      lineas: this.lineas(),
      totalDebito: this.totalDebito(),
      totalCredito: this.totalCredito(),
      diferencia: this.diferencia(),
    };

    if (this.skipBackendEnrichment) {
      this.asientoGuardado.emit(resultado);
      this.dialogRef?.close({ success: true, data: resultado, response: null });
      this.loading.set(false);
      return;
    }

    try {
      const response = await this.importarAsientoService.enviarArchivo(
        this.archivoOriginal!,
        this.tieneEncabezado(),
        this.datosCabecera,
      );

      const { lineas: lineasApi, errores: erroresApi } =
        this.importarAsientoService.parseFlatFileResponse(response);

      if (erroresApi.length > 0) {
        const mensaje =
          erroresApi.length === 1
            ? erroresApi[0]
            : `No se pudo importar el archivo:\n${erroresApi.join('\n')}`;
        this.mostrarAlertaError(mensaje);
        return;
      }

      if (this.requiereAprobacion) {
        const confirmed = await this.showConfirmDialog(
          'Documento pendiente de aprobación',
          'El documento quedará en estado "Para aprobar". ¿Desea continuar?',
          'info',
        );

        if (!confirmed) {
          this.loading.set(false);
          return;
        }

        this.mostrarAlertaExito('Documento guardado correctamente. Pendiente de aprobación.');
      } else {
        this.mostrarAlertaExito('Asiento contable guardado exitosamente. Documento aprobado.');
      }

      this.asientoGuardado.emit(resultado);
      this.dialogRef?.close({
        success: true,
        data: resultado,
        response: lineasApi.length > 0 ? { content: lineasApi } : response,
      });
    } catch (error: any) {
      const { errores: erroresApi } =
        this.importarAsientoService.parseFlatFileResponseFromHttpError(error);

      if (erroresApi.length > 0) {
        const mensaje =
          erroresApi.length === 1
            ? erroresApi[0]
            : `No se pudo importar el archivo:\n${erroresApi.join('\n')}`;
        this.mostrarAlertaError(mensaje);
        this.dialogRef?.close({ success: false, error: mensaje });
        return;
      }

      const backendMessage = this.extractBackendErrorMessage(error);
      this.mostrarAlertaError(backendMessage);
      this.dialogRef?.close({ success: false, error: backendMessage });
    } finally {
      this.loading.set(false);
    }
  }

  private extractBackendErrorMessage(error: any): string {
    const apiError = error?.error;

    if (typeof apiError === 'string' && apiError.trim()) {
      return apiError;
    }

    const { errores } = this.importarAsientoService.parseFlatFileResponseFromHttpError(error);
    if (errores.length > 0) {
      return errores.length === 1 ? errores[0] : errores.join('\n');
    }

    const content = apiError?.response?.content;
    if (content && typeof content === 'object' && !Array.isArray(content)) {
      const mensaje = content.message ?? content.detail ?? content.title;
      if (mensaje) {
        return String(mensaje);
      }
    }

    if (content && typeof content === 'string' && content.trim()) {
      return content;
    }

    if (Array.isArray(apiError?.errors) && apiError.errors.length > 0) {
      return apiError.errors
        .map((item: any) => item?.message || item?.defaultMessage || item)
        .join('. ');
    }

    if (error?.status === 500) {
      return 'El servidor no pudo procesar el archivo. Verifique el formato y las cuentas del archivo.';
    }

    return (
      apiError?.message ||
      apiError?.detail ||
      apiError?.title ||
      error?.message ||
      'Error al importar el asiento contable'
    );
  }

  async onCancel(): Promise<void> {
    if (this.lineas().length > 0 && this.lineas().some((l) => l.codCuenta)) {
      const confirmed = await this.showConfirmDialog(
        '¿Cancelar cambios?',
        'Tienes cambios sin guardar. ¿Estás seguro de que deseas cancelar?',
        'warning',
      );
      if (!confirmed) return;
    }
    this.dialogRef?.close(null);
  }

  private async showConfirmDialog(title: string, message: string, type: string): Promise<boolean> {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title,
        message,
        type,
        confirmText: 'Aceptar',
        cancelText: 'Cancelar',
      },
      size: 'sm',
    });

    const result = await dialogRef.afterClosed().toPromise();
    return result === true;
  }

  private mostrarAlertaExito(mensaje: string): void {
    this.alertaTipo.set('success');
    this.alertaTitulo.set('Éxito');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);

    setTimeout(() => {
      this.cerrarAlerta();
    }, 3000);
  }

  private mostrarAlertaError(mensaje: string): void {
    this.alertaTipo.set('error');
    this.alertaTitulo.set('Error');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);
  }

  cerrarAlerta(): void {
    this.mostrarAlerta.set(false);
  }
}
